import { ComponentProps, ComponentType, ReactNode } from 'react';
import DropdownList from 'react-widgets/DropdownList';

export interface DropdownItem {
  value: unknown;
  title?: ReactNode;
  icon?: ReactNode;
}

interface DropdownProps {
  children?: ReactNode;
  disabled?: boolean | readonly DropdownItem[];
  items?: DropdownItem[];
  onChange: (value: unknown) => void;
  onSelect?: (value: unknown) => void;
  value?: unknown;
  wrapper?: ComponentType<{ item: DropdownItem }>;
  sm?: boolean;
  groupbyKey?: string;
  placeHolder?: string;
  filter?: boolean | 'contains' | 'startsWith' | 'endsWith' | ((item: DropdownItem, searchTerm: string) => boolean);
}

const Dropdown = ({ children, disabled, items = [], onChange, onSelect = undefined, value, wrapper, sm, groupbyKey, placeHolder, filter }: DropdownProps) => {
  const selectedItem = items.find((item) => JSON.stringify(item.value) === JSON.stringify(value));
  const WrapperComponent = wrapper;

  const RenderItem = ({ item }: { item?: DropdownItem }) => {
    if (!item) {
      return null;
    }
    return (
      <>
        {WrapperComponent ? (
          <WrapperComponent item={item} />
        ) : (
          <div className="d-flex align-items-center">
            {item.icon ? <span className="me-2">{item.icon}</span> : null}
            <span>{item.title}</span>
          </div>
        )}
      </>
    );
  };

  const handleChange = (selected: DropdownItem) => {
    onChange(selected.value);
  };

  // `onSelect` fires on every pick (even when the picked item equals the
  // current value), unlike `onChange` which only fires on a change. Used by
  // `MarkHeadingDropdown` to support re-clicking the active list-structure
  // option to toggle the class off.
  const handleSelect = onSelect
    ? (selected: DropdownItem) => {
        onSelect(selected.value);
      }
    : undefined;

  return (
    <DropdownList
      disabled={disabled}
      containerClassName={sm ? 'react-widget-sm' : ''}
      onChange={handleChange}
      onSelect={handleSelect}
      dataKey="value"
      data={items}
      value={selectedItem}
      defaultValue={value}
      renderValue={children ? () => children : RenderItem}
      renderListItem={RenderItem}
      groupBy={groupbyKey}
      placeholder={placeHolder}
      filter={filter as ComponentProps<typeof DropdownList>['filter']}
      textField="title"
    />
  );
};

interface DropdownButtonProps {
  icon?: ReactNode;
  title?: ReactNode;
  disabled?: boolean;
  sm?: boolean;
}

const DropdownButton = ({ icon, title }: DropdownButtonProps) => {
  return (
    <>
      <div className="d-flex align-items-center">
        {icon ? <span className="me-2">{icon}</span> : null}
        <span>{title}</span>
      </div>
    </>
  );
};

Dropdown.DropdownButton = DropdownButton;

export default Dropdown;
